import * as React from 'react'
import { PropsWithChildren, ReactElement, useEffect } from 'react'
import { CreateDto } from '../../../config/filePartyConfig'
import { FormikConfig } from 'formik/dist/types'
import { AxiosResponse } from 'axios'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import { PartyAction } from '../../../config/partyConfig'
import Form from '../Form'
import EnumCombobox, { EnumComboboxProps } from '../../Combobox/EnumCombobox'
import { AttributeEntityDto, AttributesReturnProps } from '../../../hooks/useAttributes'
import FormikComboboxField from '../../Formik/FormikComboboxField'
import { DimensionPivotValues } from '../Accounting/DimensionFields'
import { PartyTierDto } from '../../../config/finance/financeConfig'
import { RateDto } from '../../../config/fileConfig'

export type BasePartyMutationDto = AttributeEntityDto &
    RateDto<PartyTierDto> & {
        id?: string
        shortName?: string
        addressStreet?: string
        addressNumber?: string
        addressPostalCode?: string
        addressLocality?: string
        addressMunicipality?: string
        addressCountry?: string
        email: string
        phoneLandline: string
        status: PartyStatus
        type: PartyType
        pivot?: CreateDto
        accounting?: DimensionPivotValues
        crmRelation?: boolean
        accountManagerId?: string
        documentRootId?: string
        kycFileId?: string
    }

export type AddPartyProps = Pick<CreateDto, 'role' | 'parentId'>

export enum PartyStatus {
    Active = 'ACTIVE',
    Inactive = 'INACTIVE',
}

export enum PartyType {
    Legal = 'LEGAL',
    Natural = 'NATURAL',
}

export function PartyStatusDropdown(): ReactElement {
    return (
        <FormikComboboxField<BasePartyMutationDto, EnumComboboxProps<PartyStatus>>
            labelKey={'party.status.label'}
            fieldName={'status'}
            component={EnumCombobox}
            componentProps={{
                entities: [PartyStatus.Active, PartyStatus.Inactive],
                labelKeyPrefix: 'party.status',
                clearable: false,
            }}
        />
    )
}

export type PartyProps<D extends BasePartyMutationDto> = {
    formikProps: Omit<FormikConfig<D>, 'onSubmit'>
    onSubmit: (party: D) => Promise<AxiosResponse<string>>
    goBack: () => void
    attributes: AttributesReturnProps
}

export default function PartyForm<D extends BasePartyMutationDto>(props: PropsWithChildren<PartyProps<D>>): ReactElement {
    const dispatch = useDispatch()

    useEffect(() => {
        return () => {
            dispatch({ type: PartyAction.Reset })
        }
    }, [])

    return (
        <Formik<D>
            {...props.formikProps}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async (party: D) => {
                await props.onSubmit({
                    ...party,
                    pivot: party.pivot ?? undefined,
                })
                props.goBack()
            }}
        >
            <Form>{props.children}</Form>
        </Formik>
    )
}
